import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import { Users, Search, ArrowRight } from "lucide-react";

const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") {
        if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
        if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
        return v.toLocaleString("en-IN");
    }
    return String(v);
};

export default function ClientPortfolio() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const nav = useNavigate();

    useEffect(() => {
        api.get("/clients/portfolio")
            .then(r => setData(r.data))
            .catch(e => toast.error("Load failed: " + e.message));
    }, []);

    const filtered = (data?.clients || []).filter(c =>
        !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.id.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// CLIENT PORTFOLIO</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <Users className="text-[var(--brand-amber)]" size={26} />
                    Client Portfolio
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Every client · Every project · One drill-down away
                </p>
            </div>

            {data && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Panel testId="kpi-count">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Clients</div>
                            <div className="text-2xl font-display font-black text-[var(--text-primary)] mt-1">{data.summary.count}</div>
                        </Panel>
                        <Panel testId="kpi-contract-total">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Total Contract</div>
                            <div className="text-2xl font-display font-black text-[var(--text-primary)] mt-1">{fmt(data.summary.total_contract_value)}</div>
                        </Panel>
                        <Panel testId="kpi-billed">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Billed</div>
                            <div className="text-2xl font-display font-black text-[var(--text-primary)] mt-1">{fmt(data.summary.total_billed)}</div>
                        </Panel>
                        <Panel testId="kpi-collected">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Collected</div>
                            <div className="text-2xl font-display font-black text-[var(--pos)] mt-1">{fmt(data.summary.total_collected)}</div>
                        </Panel>
                        <Panel testId="kpi-receivable">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Receivable</div>
                            <div className="text-2xl font-display font-black text-[var(--brand-amber)] mt-1">{fmt(data.summary.total_receivable)}</div>
                            <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">Avg collection eff: {data.summary.avg_collection_eff}%</div>
                        </Panel>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 border border-panel bg-[var(--bg-base)] px-2 py-1 flex-1 max-w-md">
                            <Search size={12} className="text-[var(--text-secondary)]" />
                            <input
                                data-testid="client-search"
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Filter by name or ID…"
                                className="bg-transparent text-xs font-mono-fin px-1 py-0.5 focus:outline-none flex-1"
                            />
                        </div>
                    </div>

                    <Panel testId="clients-table" title={`Clients · ${filtered.length}`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs font-body">
                                <thead>
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2 px-2">Client ID</th>
                                        <th className="text-left py-2 px-2">Name</th>
                                        <th className="text-left py-2 px-2">Type</th>
                                        <th className="text-right py-2 px-2">Contract</th>
                                        <th className="text-right py-2 px-2">Billed</th>
                                        <th className="text-right py-2 px-2">Collected</th>
                                        <th className="text-right py-2 px-2">Receivable</th>
                                        <th className="text-right py-2 px-2">Margin%</th>
                                        <th className="text-right py-2 px-2">Coll Eff%</th>
                                        <th className="text-left py-2 px-2">Risk</th>
                                        <th className="text-right py-2 px-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 && (
                                        <tr><td colSpan={11} className="text-center py-8 text-[var(--text-muted)]">
                                            No clients. Go to <span className="text-[var(--brand-amber)]">Workbook Ingest</span> to import.
                                        </td></tr>
                                    )}
                                    {filtered.map((c) => (
                                        <tr
                                            key={c.id}
                                            data-testid={`client-row-${c.id}`}
                                            className="border-b border-panel/60 hover:bg-panel/40 cursor-pointer"
                                            onClick={() => nav(`/clients/${c.id}`)}
                                        >
                                            <td className="py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{c.id}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]">{c.name}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/70 text-[11px]">{c.type}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--text-primary)]">{fmt(c.contract_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(c.billed)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--pos)]">{fmt(c.collected)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(c.receivable)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">
                                                <span className={c.margin_pct < 10 ? "text-[var(--neg)]" : "text-[var(--text-primary)]/80"}>{c.margin_pct}%</span>
                                            </td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">
                                                <span className={c.collection_eff < 60 ? "text-[var(--neg)]" : "text-[var(--pos)]"}>{c.collection_eff}%</span>
                                            </td>
                                            <td className="py-1.5 px-2 text-[11px]">{c.risk_band}</td>
                                            <td className="text-right py-1.5 px-2">
                                                <ArrowRight size={12} className="text-[var(--brand-amber)] inline" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </>
            )}
        </div>
    );
}
